<template lang="pug">
.download-page
  .text-center
    img(src='/img/ilustrations/mailbox-download.svg')
  .text-center.mt-4.mb-3
    template(v-if='documents.length > 1')
      button.btn.btn-thin.mb-3.ms-md-2(
        @click='downloadDocumentsBackgroundSheet'
      ) {{ $t('button.downloadSignaturePages') }}
      button.btn.btn-primary.mb-3.ms-md-2(@click='downloadDocuments') {{ $t('button.downloadSignedDocuments') }}
    template(v-else)
      a(
        :href='documents[0].links.background_sheet'
        download
        target='_blank'
      )
        button.btn.btn-thin.mb-3.ms-md-2 {{ $t('button.downloadSignaturePage') }}
      a(
        :href='documents[0].links.download'
        download
        target='_blank'
      )
        button.btn.btn-primary.mb-3.ms-md-2 {{ $t('button.downloadSignedDocument') }}
    template(v-if='envelope.state === "completed"')
      button.btn.btn-thin.mb-3.ms-md-2(
        @click='downloadEnvelopeDocumentsAndBackgroundSheet'
      ) {{ $t('button.downloadEnvelopeCompleted') }}
  h6.h-title {{ $t('pages.download.documents') }}
  template(v-for='doc in documents')
    .card.card--file.mb-2(:key='doc.id')
      .card-body.d-flex
        icon.icon.me-2(name='file')
        .name {{ doc.name }}
        .dropdown.ms-auto
          button#dropdownMenuButton1.dropdown-toggle.btn-actions(
            aria-expanded='false'
            data-bs-toggle='dropdown'
            type='button'
          )
            icon(name='ellipsis-vertical')
          ul.dropdown-menu(aria-labelledby='dropdownMenuButton1')
            li
              a.dropdown-item(
                :href='doc.links.download'
                download
                target='_blank'
              ) {{ $t('button.downloadSignedDocument') }}
            li
              a.dropdown-item(
                :href='doc.links.background_sheet'
                download
                target='_blank'
              ) {{ $t('button.downloadSignaturePage') }}
            li(v-if='envelope.state === "completed"')
              a.dropdown-item(
                :href='this'
                @click.prevent='downloadEnvelopeDocumentsAndBackgroundSheet'
              ) {{ $t('button.downloadEnvelopeCompleted') }}
  h6.h-title.mt-4 {{ $t('pages.download.envelopeDetails') }}
  .card
    .card-body
      ul.list-unstyled.m-0
        li.mb-1 {{ $t('pages.download.sentAt') }}: {{ this.envelope.created_at | date }}
        li.mb-1 {{ $t('pages.download.signatureDeadline') }}:
          span.ms-1
            template(v-if='this.envelope.expire_at') {{ this.envelope.expire_at | date }}
            template(v-else) {{ $t('pages.download.noDeadline') }}
        li.mb-1 {{ $t('pages.download.envelopeExpiration') }}:
          span.ms-1
            template(v-if='this.envelope.effective_date_reminder_at')
              | {{ this.envelope.effective_date_reminder_at | date }}
            template(v-else) {{ $t('pages.download.noExpiration') }}
        li {{ $t('pages.download.envelopeStatus') }}:
          |
          IconStatus.ms-2.me-1(:iconStatus='this.envelope.state')
          span.text {{ $t(this.envelope.state) }}

  h6.h-title.mt-4 {{ $t('pages.download.recipients') }}
  .card
    .card-body.p-0
      template(v-for='recipient in recipients')
        ul.m-0.p-3.recipient-item.list-unstyled(:key='recipient.id')
          li
            span(style='font-weight: 600') {{ recipient.addressees[0].name }}
          li(v-if='recipient.addressees[0].via !== "face-to-face"') {{ recipient.addressees[0].value }}
          li
            info-pill(
              :active='signerTypes.includes(recipient.type)'
              :icon='signerTypes.includes(recipient.type) ? "signature" : "eye"'
            ) {{ recipientLabel(recipient.type) }}
          li
            IconStatus.me-1(:iconStatus='recipient.state')
            span.text {{ $t(recipient.state) }}
          template(v-for='(identifier, index) in recipient.identifiers')
            li(v-if='identifier.value') {{ identifier.title }}: {{ identifier.value }}
          li(
            v-if='documents.length > 1 && signerTypes.includes(recipient.type)'
          )
            | {{ $t('modal.envelope.documents', { total: recipientsSignaturesSummary.find((r) => r.id === recipient.id).count }) }}
            |
            | {{ $t('modal.envelope.documentsTotal', { total: documents.length }) }}
</template>
<script>
import Icon from '@/ui/atoms/Icon.vue';
import IconStatus from '@/ui/atoms/IconStatus.vue';
import RecipientsService from '@/modules/business/services/http/envelopes/RecipientsService';
import EnvelopesPublicService from '@/modules/business/services/http/envelopes/EnvelopesPublicService';
import EnvelopeDownloadsFactory from '@/modules/business/common/EnvelopeDownloads/EnvelopeDownloadsFactory';
import RecipientType from '@/enums/RecipientType';
import InfoPill from '@/modules/business/components/molecules/InfoPill.vue';

export default {
  name: 'DownloadEnvelopePage',
  components: {
    Icon,
    IconStatus,
    InfoPill,
  },
  computed: {
    token() {
      return this.$route.params.token;
    },
    isPublic() {
      return this.$route.name.split('.').shift() === 'public';
    },
    isEnvelopeToken() {
      return this.$route.params.envelope ?? false;
    },
    recipientsSignaturesSummary() {
      const total = this.documents.length;
      return this.recipients.map((recipient) => {
        const count = this.documents.filter((doc) =>
          this.recipientSignsDocument(recipient, doc)
        ).length;
        return { id: recipient.id, count, total };
      });
    },
  },
  data() {
    return {
      envelope: {},
      documents: [],
      recipients: [],
      signerTypes: ['signer', 'approver', 'in-person-signer'],
    };
  },
  async created() {
    this.$loading(true);
    this.$store.dispatch('page/setClass', 'envelope-download');

    await this.getEnvelopeData(this.token);

    const pageTitle = this.$t('pages.download.pageTitle');
    this.$store.dispatch('page/setTitle', pageTitle);
    const head = {
      title: pageTitle,
    };
    if (!this.isPublic) {
      head.action = () => {
        this.$router.push({ name: 'business.documents.inbox' });
      };
    }
    this.$store.dispatch('page/setHead', head);
    this.$loading(false);
  },
  methods: {
    recipientSignsDocument(recipient, doc) {
      if (!recipient) return false;
      return !(recipient.exclude_documents ?? []).includes(doc.id);
    },
    recipientLabel(recipientType) {
      const typeValue = RecipientType.values.filter(
        (type) => type.value === recipientType
      );
      if (!typeValue.length) return '';
      return typeValue[0].value !== 'carbon-copy'
        ? this.$t(`modal.envelope.${typeValue[0].value}`)
        : this.$t('modal.envelope.viewer');
    },
    async getEnvelopeData(token) {
      const res = this.isEnvelopeToken
        ? await EnvelopesPublicService.listSpecificEnvelope(token)
        : await RecipientsService.listEspecificRecipientEnvelope(token);

      this.envelope = res.data.envelope ?? res.data;
      this.documents = res.data.envelope?.documents ?? res.data.documents;
      this.recipients = res.data.envelope?.recipients ?? res.data.recipients;
    },
    async downloadDocuments() {
      const envelopeDownloads =
        EnvelopeDownloadsFactory.createEnvelopeDownloads(this.token, 'public');

      await envelopeDownloads.downloadDocuments(this.envelope.title);
    },
    async downloadDocumentsBackgroundSheet() {
      const envelopeDownloads =
        EnvelopeDownloadsFactory.createEnvelopeDownloads(this.token, 'public');

      await envelopeDownloads.downloadSignaturePage(this.envelope.title);
    },
    async downloadEnvelopeDocumentsAndBackgroundSheet() {
      const envelopeDownloads =
        EnvelopeDownloadsFactory.createEnvelopeDownloads(this.token, 'public');

      await envelopeDownloads.downloadEnvelopeDocumentsAndBackgroundSheet(
        this.envelope.title
      );
    },
  },
};
</script>
<style lang="stylus">
.download-page
  padding-bottom: 5rem
  .card--file
    .icon
      color: #CCCCCC
      margin-top: 4px
    .btn-actions
      width: 1.2em
      height: 1.5em
      .icon
        color: rgb(85, 85, 85)
        font-size: 1.2rem
        display: contents
  .recipient-item
    border-bottom: 1px solid #CCCCCC
    &:last-child
      border-bottom: none
</style>
